/**
 * @author: lv.chen
 * @date: 2025/7/11
 * @desc: 按日期粒度分组的Metric选择器组件
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { isFunction } from 'lodash-es';
import { Segment } from 'tea-component';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { getMetricName } from '@intelligence/v3/components/TableComponents/RatioMetricSortBy/utils';

import './index.scss';

/**
 * 获取特殊指标名的方法
 */
type IMetricNameSpecialGetter = (metric: IPrimaryKeyMetric, metricName: string) => string;

/**
 * 获取当前选中指标数据源icon的方法（特殊处理，比如newzoo的icon 没选中的时候是一个icon，选中后又是另外一种icon）
 */
type IDataSourceHighlightIconGetter = (dataSource: string) => string;

interface IGroupByGranularityMetricSelectProps extends IBaseProps {
  getMetricNameSpecial?: IMetricNameSpecialGetter;
  getDataSourceHighlightIcon?: IDataSourceHighlightIconGetter;
  options: IMetricsGroupByGranularity[];
  value: IPrimaryKeyMetric;
  /**
   * 切换指标的回调
   * @param value 指标key
   * @param granularity 指标所属的日期粒度
   * @param context 组件change的context
   */
  onChange?: (value: IPrimaryKeyMetric, granularity: IGranularity, context: any) => void;
}

const GRANULARITY_NAME_LOCALE_KEY_MAP: Record<IGranularity, string> = {
  daily: 'dbi_daily',
  weekly: 'dbi_weekly',
  monthly: 'dbi_monthly',
  quarterly: 'dbi_quarterly',
  yearly: 'dbi_annually',
};

const formatMetricOptions = (
  locale: string,
  key: string,
  metrics: IPrimaryKeyMetric[] | null,
  getMetricNameSpecial?: IMetricNameSpecialGetter,
  getDataSourceHighlightIcon?: IDataSourceHighlightIconGetter,
) => {
  if (!Array.isArray(metrics) || metrics.length === 0) return [];

  const useDataSourceIconHighlight = isFunction(getDataSourceHighlightIcon);
  return metrics.map((metric) => {
    const dataSourceKey = metric?.dataSource;
    const defaultDataSourceIcon = getDataSourceIcon(dataSourceKey);
    const dataSourceIcon =
      !useDataSourceIconHighlight || key !== metric?.key
        ? defaultDataSourceIcon
        : getDataSourceHighlightIcon(metric?.dataSource) || defaultDataSourceIcon;
    const metricKey = metric?.metric;
    let metricName = getMetricName(locale, dataSourceKey, metricKey);
    metricName = getMetricNameSpecial?.(metric, metricName) ?? metricName;
    return {
      value: metric?.key,
      text: (
        <div className="dbi-datasource-metric">
          <img className="dbi-datasource-metric__icon" src={dataSourceIcon} alt="" />
          <span className="dbi-datasource-metric__text">{metricName}</span>
        </div>
      ),
      payload: metric,
    };
  });
};
const GroupByGranularityMetricSelect: React.FC<IGroupByGranularityMetricSelectProps> = (props) => {
  const { style, className, getMetricNameSpecial, getDataSourceHighlightIcon, options, value, onChange, isNoGranularityName = false } = props;


  const { locale, formatMessage } = useIntl();

  if (!Array.isArray(options) || options.length === 0) return null;
  return (
    <div style={style} className={classnames('dbi-group-by-granularity-metric-select', className)}>
      <ul className="dbi-group-by-granularity-metric-select__granularity-list">
        {options.map((option) => {
          const granularity = option?.granularity;
          const metrics = option?.metrics;
          const granularityName = formatMessage({ id: GRANULARITY_NAME_LOCALE_KEY_MAP[granularity] });
          const metricOptions = formatMetricOptions(
            locale,
            value?.key,
            metrics,
            getMetricNameSpecial,
            getDataSourceHighlightIcon,
          );
          return (
            <li key={granularity} className="dbi-group-by-granularity-metric-select__granularity-item">
              {!isNoGranularityName && <div className="dbi-group-by-granularity-metric-select__granularity-name">{granularityName}</div>}
              <Segment
                options={metricOptions}
                value={value?.key}
                onChange={(value, context) => {
                  const metricOption = metricOptions.find((option) => value === option?.value);
                  const metricPayload = metricOption?.payload || { dataSource: '', metric: '' };
                  const dataSource = metricPayload?.dataSource;
                  const metric = metricPayload?.metric;
                  onChange?.({ key: value, dataSource, metric, granularity }, granularity, context);
                }}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default GroupByGranularityMetricSelect;
